import { InfoCircledIcon } from '@radix-ui/react-icons'
import * as RadixTooltip from '@radix-ui/react-tooltip'
import classNames from 'classnames'
import { useState } from 'react'
import * as styles from './tooltip.css.ts'

interface TooltipProps {
  children: React.ReactNode
  iconClassName?: string
  contentClassName?: string
  side?: 'top' | 'right' | 'bottom' | 'left'
  sideOffset?: number
  width?: number
  label?: string
}

// Necessary to enable tooltip on click on mobile since Radix designers won't add this functionality
// https://github.com/radix-ui/primitives/issues/955
function TooltipWithClickToOpen({ children, iconClassName, contentClassName, side = 'top', sideOffset = 3, width, label }: TooltipProps) {
  const [open, setOpen] = useState(false)

  return (
    <RadixTooltip.Provider>
      <RadixTooltip.Root open={open} delayDuration={0} onOpenChange={setOpen}>
        <RadixTooltip.Trigger asChild>
          <button onClick={() => setOpen(true)} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }} aria-label={label}>
            <InfoCircledIcon className={classNames(styles.infoIcon, iconClassName)} />
          </button>
        </RadixTooltip.Trigger>
        <RadixTooltip.Portal>
          <RadixTooltip.Content className={classNames(styles.tooltip, contentClassName)} sideOffset={sideOffset} side={side}>
            <div className={styles.tooltipContent} style={width ? { width: `${width}px` } : { maxWidth: '300px' }}>
              {children}
            </div>
          </RadixTooltip.Content>
        </RadixTooltip.Portal>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  )
}

type TooltipWithTriggerProps = {
  trigger: React.ReactElement<any>
  children: React.ReactElement<any>
  maxWidth?: number
  side?: 'top' | 'right' | 'bottom' | 'left'
  size?: 'small' | 'normal'
  offset?: number
}

export const TooltipWithHover = ({ trigger, children, maxWidth = 300, side = 'top', size = 'normal', offset = 5 }: TooltipWithTriggerProps) => {
  return (
    <RadixTooltip.Provider>
      <RadixTooltip.Root delayDuration={0}>
        <RadixTooltip.Trigger asChild>{trigger}</RadixTooltip.Trigger>
        <RadixTooltip.Content
          className={classNames(styles.tooltip, {
            [styles.smallTooltip]: size === 'small',
          })}
          style={{ maxWidth: maxWidth }}
          sideOffset={offset}
          side={side}
        >
          <div className={styles.tooltipContent}>{children}</div>
        </RadixTooltip.Content>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  )
}

export { TooltipWithClickToOpen as Tooltip }
